import { useSyncExternalStore } from "react";
import { updateAccountEmail } from "./storage";

// Tiny external store for auth status, mirroring the storage.ts pattern so
// useSyncExternalStore snapshots stay referentially stable.

export type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | {
      status: "authed";
      email: string;
      isAdmin: boolean;
      emailVerified: boolean;
      username: string | null;
      displayName: string | null;
    };

type ApiUser = {
  email: string;
  is_admin?: boolean;
  email_verified?: boolean;
  username?: string | null;
  display_name?: string | null;
};

function authedFrom(user: ApiUser): Extract<AuthState, { status: "authed" }> {
  return {
    status: "authed",
    email: user.email,
    isAdmin: user.is_admin === true,
    emailVerified: user.email_verified === true,
    username: user.username ?? null,
    displayName: user.display_name ?? null,
  };
}

let authState: AuthState = { status: "loading" };
const listeners = new Set<() => void>();

function set(next: AuthState) {
  authState = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): AuthState {
  return authState;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === "string") return body.error;
  } catch {
    /* fall through */
  }
  return `Request failed (${res.status}).`;
}

export async function refreshAuth(): Promise<void> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "same-origin" });
    if (res.ok) {
      const body = (await res.json()) as { user: ApiUser };
      set(authedFrom(body.user));
    } else {
      set({ status: "anon" });
    }
  } catch {
    set({ status: "anon" });
  }
}

// Called by the profile-update flow so the AuthState reflects the new
// username / display_name without another /me round-trip.
export function applyAuthUser(user: ApiUser): void {
  set(authedFrom(user));
}

type ScalarFields = Record<
  string,
  string | number | boolean | null | undefined
>;
function emit(name: string, fields: ScalarFields = {}): void {
  import("./events")
    .then((m) => m.logEvent(name, fields))
    .catch(() => {});
}

// Any auth call: on 429, fire the shared ratelimit.hit event so a
// credential-stuffing attack throttling login shows up in the debug
// timeline just like the api.ts callers do.
function noteRateLimit(res: Response, endpoint: string): void {
  if (res.status !== 429) return;
  const retry = Number(res.headers.get("Retry-After") ?? "");
  emit("ratelimit.hit", {
    endpoint,
    retry_after: Number.isFinite(retry) ? retry : 0,
  });
}

async function submit(
  path: string,
  email: string,
  password: string,
): Promise<void> {
  emit("auth.login", { path });
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, password }),
  });
  noteRateLimit(res, path);
  if (!res.ok) {
    emit("auth.login_error", { path, status: res.status });
    throw new Error(await readError(res));
  }
  const body = (await res.json()) as { user: ApiUser };
  set(authedFrom(body.user));
  emit("auth.login_ok", { path });
}

export function login(email: string, password: string): Promise<void> {
  return submit("/api/auth/login", email, password);
}

export interface RegisterResult {
  // True when the deployment runs the verify-before-signin flow. The
  // request didn't return a session; the user has to click the link in
  // the email to actually sign in. UI should show a "check your email"
  // message rather than treating the user as authed.
  verificationPending: boolean;
}

// Where landing-URL referral codes live between page load and
// account creation. sessionStorage rather than localStorage so a
// stale code from months ago doesn't attribute unfairly if the user
// wanders back.
const REFERRAL_STORAGE_KEY = "reefscope_referral_v1";
export function captureReferralFromUrl(): void {
  try {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code) {
      const valid = /^[A-Z0-9]{4,12}$/i.test(code);
      if (valid) {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, code.toUpperCase());
      }
      // Fire-and-forget event so we can build the "landed via ref →
      // signed up" funnel. Async import keeps auth.ts free of the
      // events dep for callers that don't need it.
      import("./events")
        .then((m) => {
          m.logEvent("referral.capture", { valid });
        })
        .catch(() => {});
    }
  } catch {
    /* sessionStorage blocked — silently drop attribution */
  }
}
function pendingReferralCode(): string | null {
  try {
    return sessionStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export async function register(
  email: string,
  password: string,
): Promise<RegisterResult> {
  const referral_code = pendingReferralCode() ?? undefined;
  if (referral_code) {
    import("./events")
      .then((m) => {
        m.logEvent("referral.attach_attempt", {});
      })
      .catch(() => {});
  }
  emit("auth.register", { has_referral: Boolean(referral_code) });
  const res = await fetch("/api/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, password, referral_code }),
  });
  noteRateLimit(res, "/api/auth/register");
  if (!res.ok) {
    emit("auth.register_error", { status: res.status });
    throw new Error(await readError(res));
  }
  const body = (await res.json()) as {
    verification_pending?: boolean;
    user?: ApiUser;
  };
  // Clear stashed code once used so subsequent signups (rare in
  // practice) don't double-attribute.
  try {
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (body.verification_pending) {
    emit("auth.register_ok", { verification_pending: true });
    return { verificationPending: true };
  }
  if (body.user) {
    set(authedFrom(body.user));
    emit("auth.register_ok", { verification_pending: false });
    return { verificationPending: false };
  }
  emit("auth.register_ok", { verification_pending: true, no_body: true });
  return { verificationPending: true };
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  emit("auth.password_change", {});
  const res = await fetch("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
  noteRateLimit(res, "/api/auth/change-password");
  if (!res.ok) {
    emit("auth.password_change_error", { status: res.status });
    throw new Error(await readError(res));
  }
  emit("auth.password_change_ok", {});
}

export async function changeEmail(
  newEmail: string,
  currentPassword: string,
): Promise<void> {
  emit("auth.email_change", {});
  const res = await fetch("/api/auth/change-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      new_email: newEmail,
      current_password: currentPassword,
    }),
  });
  noteRateLimit(res, "/api/auth/change-email");
  if (!res.ok) {
    emit("auth.email_change_error", { status: res.status });
    throw new Error(await readError(res));
  }
  const body = (await res.json()) as { user: ApiUser };
  // Keep the email-keyed local cache pointed at this account before the
  // auth-state change re-triggers App's account effect (which would
  // otherwise treat the new email as a different account and wipe it).
  updateAccountEmail(body.user.email);
  set(authedFrom(body.user));
  emit("auth.email_change_ok", {});
}

// ---- password reset (unauthenticated flow) ----

export async function forgotPassword(email: string): Promise<void> {
  emit("auth.forgot", {});
  const res = await fetch("/api/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email }),
  });
  noteRateLimit(res, "/api/auth/forgot-password");
  if (!res.ok) {
    emit("auth.forgot_error", { status: res.status });
    throw new Error(await readError(res));
  }
  emit("auth.forgot_ok", {});
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<void> {
  emit("auth.reset", {});
  const res = await fetch("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  noteRateLimit(res, "/api/auth/reset-password");
  if (!res.ok) {
    emit("auth.reset_error", { status: res.status });
    throw new Error(await readError(res));
  }
  emit("auth.reset_ok", {});
}

// ---- email verification ----

export async function verifyEmail(token: string): Promise<void> {
  emit("auth.verify_email", {});
  const res = await fetch("/api/auth/verify-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ token }),
  });
  noteRateLimit(res, "/api/auth/verify-email");
  if (!res.ok) {
    emit("auth.verify_email_error", { status: res.status });
    throw new Error(await readError(res));
  }
  emit("auth.verify_email_ok", {});
}

export async function resendVerification(): Promise<void> {
  emit("auth.resend_verification", {});
  const res = await fetch("/api/auth/send-verification", {
    method: "POST",
    credentials: "same-origin",
  });
  noteRateLimit(res, "/api/auth/send-verification");
  if (!res.ok) {
    emit("auth.resend_verification_error", { status: res.status });
    throw new Error(await readError(res));
  }
}

// ---- account deletion (right-to-erasure) ----

// Double validation: the server requires BOTH the type-to-confirm token
// ("DELETE") and the current password before erasing the account.
export async function deleteAccount(
  currentPassword: string,
  confirm: string,
): Promise<void> {
  emit("auth.delete_account", {});
  const res = await fetch("/api/auth/delete-account", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ current_password: currentPassword, confirm }),
  });
  noteRateLimit(res, "/api/auth/delete-account");
  if (!res.ok) {
    emit("auth.delete_account_error", { status: res.status });
    throw new Error(await readError(res));
  }
  emit("auth.delete_account_ok", {});
}

// ---- data export ----

// Streams the user's full account+state as a JSON file the browser downloads.
// The server sets Content-Disposition so a simple anchor-click works.
export function exportDataUrl(): string {
  return "/api/auth/export";
}

export interface ClaimResult {
  recovered: boolean;
  reason?: string;
  counts?: {
    lifelist: number;
    visited_locations: number;
    trips: number;
    badges: number;
  };
}

// One-time: pull the pre-auth ("solo") data onto this admin account.
export async function claimLegacy(): Promise<ClaimResult> {
  const res = await fetch("/api/auth/claim-legacy", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as ClaimResult;
}

// Local caches to wipe on sign-out so a shared device (family, hostel)
// never leaks one account's data to the next. Add prefixes here as new
// per-account caches appear.
const LOCAL_ACCOUNT_CACHE_PREFIXES = ["reefscope:plan-cache-v1:"];

function clearAccountCaches(): void {
  try {
    const rm: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (LOCAL_ACCOUNT_CACHE_PREFIXES.some((p) => k.startsWith(p))) rm.push(k);
    }
    for (const k of rm) localStorage.removeItem(k);
  } catch {
    /* storage disabled — nothing to clear */
  }
}

export async function logout(): Promise<void> {
  emit("auth.logout", {});
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
    });
  } catch {
    /* clear locally regardless */
  }
  clearAccountCaches();
  try {
    const { resetEventLocals } = await import("./events");
    resetEventLocals();
  } catch {
    /* diagnostics reset is best-effort */
  }
  set({ status: "anon" });
}
